import { Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { PROJECT_AREA_TREE, DEFAULT_AREA, VALID_AREA_KEYS, parentGroupKey } from "@/lib/project_areas";
import { phaseStyle, DEFAULT_PHASE } from "@/lib/project_phases";
import GlobalSearchModal from "@/components/GlobalSearchModal";

/**
 * ProjectShell · sidebar contextual del proyecto (Mockup D #s2 / #s3).
 *
 * Sustituye al `Header.jsx` oscuro y a la fila de TabButton horizontales
 * dentro de `ProjectEditor.jsx` cuando estamos en `/project/:id`.
 *
 * Estructura:
 *  · Cabecera con flecha "← Catálogo", logo y toggle.
 *  · Bloque del proyecto activo (título + tag de fase).
 *  · Lista de áreas (Portada · Dossier · Editor · Casting · Calendario ·
 *    Presupuesto · Financiación · Pipeline · Notas).
 *  · Otros proyectos (atajo de cambio rápido).
 *  · Pie con usuario.
 *
 * NO altera el editor de guion: el `ScreenplayTab` mantiene su layout interno
 * intacto. Cuando el editor entra en modo fullscreen, su `fixed inset-0 z-50`
 * tapa el sidebar automáticamente — sin tocar este componente.
 *
 * El área activa se lee del queryparam `?tab=` (compatible con la lógica
 * pre-existente de `ProjectEditor.jsx`). Default: `overview`.
 */

const COLLAPSE_KEY = "shell:project-nav-collapsed";

export default function ProjectShell({ children }) {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tab = (() => {
    const raw = searchParams.get("tab") || DEFAULT_AREA;
    return VALID_AREA_KEYS.has(raw) ? raw : DEFAULT_AREA;
  })();

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch (_e) { return false; }
  });
  const [proj, setProj] = useState(null);
  const [others, setOthers] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);

  // Atajo ⌘K / Ctrl+K (también dentro del proyecto)
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  // Grupos expandidos por defecto: el grupo del tab activo + "guion" siempre
  const activeGroup = parentGroupKey(tab);
  const [openGroups, setOpenGroups] = useState(() => {
    const init = new Set(["guion"]);
    if (activeGroup) init.add(activeGroup);
    return init;
  });
  useEffect(() => {
    if (activeGroup) {
      setOpenGroups((prev) => {
        if (prev.has(activeGroup)) return prev;
        const next = new Set(prev);
        next.add(activeGroup);
        return next;
      });
    }
  }, [activeGroup]);

  useEffect(() => {
    let cancel = false;
    api.get("/icaa/projects").then((r) => {
      if (!cancel) {
        const all = r.data || [];
        const active = all.find((p) => p.id === id) || null;
        setProj(active);
        setOthers(all.filter((p) => p.id !== id).slice(0, 6));
      }
    }).catch(() => {});
    return () => { cancel = true; };
  }, [id, location.pathname]);

  const toggle = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch (_e) { /* ignore */ }
      return next;
    });
  };

  const goTab = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === DEFAULT_AREA) next.delete("tab"); else next.set("tab", key);
    navigate({ pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : "" });
  };

  const initials = (user?.name || user?.email || "?").split(" ").filter(Boolean)
    .slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  const phaseSty = phaseStyle(proj?.phase || DEFAULT_PHASE);

  return (
    <div className="flex min-h-screen bg-gb-bg text-gb-black">
      {/* SIDEBAR oscuro */}
      <aside
        data-testid="project-sidebar"
        className={`sticky top-0 flex h-screen shrink-0 flex-col bg-gb-black text-white/80 transition-all duration-200 ${
          collapsed ? "w-[64px]" : "w-[260px]"
        }`}
      >
        {/* Head */}
        <div className={`flex items-center gap-2 border-b border-white/8 px-4 py-4 ${collapsed ? "justify-center px-2" : ""}`}>
          <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="project-back-link">
            <span className="text-white/55 transition group-hover:text-white">←</span>
            {!collapsed && (
              <span className="truncate text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-white">
                Catálogo
              </span>
            )}
          </Link>
          <button
            onClick={toggle}
            data-testid="project-shell-toggle"
            className="ml-auto rounded text-white/40 transition hover:text-white"
            title={collapsed ? "Expandir menú" : "Colapsar menú"}
          >
            {collapsed ? "›" : "‹"}
          </button>
        </div>

        {/* Bloque del proyecto activo */}
        {!collapsed && proj && (
          <div className="border-b border-white/8 px-4 py-4">
            <p className="truncate font-serif text-base font-medium text-white" data-testid="project-shell-title">
              {proj.title}
            </p>
            <div className="mt-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-white/55">
              <span className="inline-block h-1.5 w-1.5 rounded-full"
                    style={{ background: phaseSty.hex }} />
              <span>{phaseSty.label}</span>
              <span>·</span>
              <span className="truncate">{proj.project_type}</span>
            </div>
          </div>
        )}

        {/* Áreas del proyecto */}
        <div className="flex-1 overflow-y-auto px-2 py-3 text-sm">
          {!collapsed && (
            <div className="mb-2 px-3 text-[9px] font-semibold uppercase tracking-[0.22em] text-white/35">
              Trabajar en
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            {PROJECT_AREA_TREE.map((item) => {
              if (item.type === "group") {
                const isOpen = openGroups.has(item.key) || collapsed;
                const hasActiveChild = item.children?.some((c) => c.key === tab);
                return (
                  <div key={item.key}>
                    <button
                      onClick={() => {
                        setOpenGroups((prev) => {
                          const next = new Set(prev);
                          if (next.has(item.key)) next.delete(item.key); else next.add(item.key);
                          return next;
                        });
                      }}
                      data-testid={`project-nav-group-${item.key}`}
                      title={collapsed ? item.label : undefined}
                      className={`flex w-full items-center gap-2.5 rounded-md px-3 py-1.5 text-[13px] text-left transition ${
                        hasActiveChild ? "text-white" : "text-white/65 hover:text-white"
                      } ${collapsed ? "justify-center" : ""}`}
                    >
                      <span className="w-4 shrink-0 text-center text-[12px]">{item.icon}</span>
                      {!collapsed && (
                        <>
                          <span className="flex-1 truncate text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55">
                            {item.label}
                          </span>
                          <span className="text-[10px] text-white/35">{isOpen ? "▾" : "▸"}</span>
                        </>
                      )}
                    </button>
                    {isOpen && (
                      <div className={collapsed ? "" : "ml-2 border-l border-white/8 pl-2"}>
                        {item.children.map((c) => (
                          <AreaButton key={c.key} area={c} active={c.key === tab}
                                      collapsed={collapsed} onClick={() => goTab(c.key)} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <AreaButton key={item.key} area={item} active={item.key === tab}
                            collapsed={collapsed} onClick={() => goTab(item.key)} />
              );
            })}
          </div>

          {/* Otros proyectos */}
          {!collapsed && others.length > 0 && (
            <div className="mt-6">
              <div className="mb-2 px-3 text-[9px] font-semibold uppercase tracking-[0.22em] text-white/35">
                Otros proyectos
              </div>
              <div className="flex flex-col gap-0.5">
                {others.map((p) => (
                  <Link key={p.id}
                    to={`/project/${p.id}`}
                    data-testid={`project-nav-other-${p.id}`}
                    className="flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[12px] text-white/55 transition hover:bg-white/[0.06] hover:text-white">
                    <span className="w-4 shrink-0 text-center text-[11px]">▸</span>
                    <span className="flex-1 truncate">{p.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/8 px-3 py-3">
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="project-shell-search"
            title="Buscar (⌘K)"
            className={`mb-3 flex w-full items-center gap-2 rounded-md border border-white/10 bg-white/[0.04] px-2 py-2 text-left text-xs text-white/55 transition hover:border-white/25 hover:text-white ${
              collapsed ? "justify-center" : ""
            }`}
          >
            <span>⌕</span>
            {!collapsed && (
              <>
                <span className="flex-1 truncate">Buscar</span>
                <kbd className="font-mono text-[10px] opacity-60">⌘K</kbd>
              </>
            )}
          </button>
          {user && (
            <div className={`flex items-center gap-2 ${collapsed ? "justify-center" : ""}`}>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gb-red text-[11px] font-bold text-white">
                {initials}
              </span>
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white">
                    {user.name?.split(" ")[0] || user.email?.split("@")[0]}
                  </p>
                  <button
                    onClick={logout}
                    data-testid="project-shell-logout"
                    className="text-[10px] uppercase tracking-widest text-white/40 transition hover:text-gb-red"
                  >
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* MAIN */}
      <main className="min-w-0 flex-1">{children}</main>

      <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}

const AreaButton = ({ area, active, collapsed, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`project-nav-${area.key}`}
    title={collapsed ? area.label : undefined}
    className={`flex w-full items-center gap-2.5 rounded-md px-3 py-1.5 text-[13px] text-left transition ${
      active
        ? "bg-gb-red font-medium text-white"
        : area.accent
          ? "text-white hover:bg-white/[0.06]"
          : "text-white/78 hover:bg-white/[0.06] hover:text-white"
    } ${collapsed ? "justify-center" : ""}`}
  >
    <span className="w-4 shrink-0 text-center text-[12px]">{area.icon}</span>
    {!collapsed && <span className="flex-1 truncate">{area.label}</span>}
  </button>
);
